import { useCallback, useEffect, useRef, useState } from 'react';
import { Siren } from 'lucide-react';
import { apiUrl } from '../config/api';
import { getCurrentPosition } from '../utils/location';
import { apiFetch, isLoggedIn } from '../utils/session';
import './GlobalSOSButton.css';

const HOLD_MS = 3000;
const R = 26;
const C = 2 * Math.PI * R;

function GlobalSOSButton() {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const rafRef = useRef(null);
  const startTsRef = useRef(null);

  const cancelRaf = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const endHold = useCallback(() => {
    cancelRaf();
    startTsRef.current = null;
    setHolding(false);
    setProgress(0);
  }, [cancelRaf]);

  const triggerSos = useCallback(async () => {
    if (!isLoggedIn()) {
      setToast('Sign in from the login page to use SOS.');
      return;
    }

    setBusy(true);
    setToast('Getting your live location…');

    try {
      const pos = await getCurrentPosition();
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;

      const res = await apiFetch(apiUrl('/sos/trigger'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'SOS could not be sent');
      }
      setToast('SOS sent with your live location. Area coordinators have been alerted.');
    } catch (e) {
      const msg =
        e.code === 1
          ? 'Location permission denied — enable GPS to send SOS.'
          : e.message || 'Could not send SOS.';
      setToast(msg);
    } finally {
      setBusy(false);
    }
  }, []);

  const tick = useCallback(() => {
    const start = startTsRef.current;
    if (start == null) return;
    const elapsed = Date.now() - start;
    const p = Math.min(1, elapsed / HOLD_MS);
    setProgress(p);
    if (p >= 1) {
      cancelRaf();
      startTsRef.current = null;
      setHolding(false);
      setProgress(0);
      triggerSos();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [cancelRaf, triggerSos]);

  const onPointerDown = (e) => {
    if (busy) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    cancelRaf();
    startTsRef.current = Date.now();
    setHolding(true);
    setProgress(0);
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerEnd = () => {
    if (progress < 1) {
      endHold();
    }
  };

  useEffect(() => {
    const stop = () => endHold();
    window.addEventListener('blur', stop);
    return () => window.removeEventListener('blur', stop);
  }, [endHold]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const offset = C * (1 - progress);

  return (
    <>
      <div className="sos-fab-wrap" aria-label="Emergency SOS">
        <button
          type="button"
          className={`sos-fab${holding ? ' sos-fab-holding' : ''}${busy ? ' sos-fab-busy' : ''}`}
          disabled={busy}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onPointerLeave={onPointerEnd}
        >
          <svg className="sos-ring" viewBox="0 0 72 72" aria-hidden>
            <circle className="sos-ring-bg" cx="36" cy="36" r={R} />
            <circle
              className="sos-ring-progress"
              cx="36"
              cy="36"
              r={R}
              strokeDasharray={C}
              strokeDashoffset={offset}
              transform="rotate(-90 36 36)"
            />
          </svg>
          <span className="sos-fab-inner">
            <Siren size={28} strokeWidth={2.25} aria-hidden />
          </span>
        </button>
        {holding ? <span className="sos-hint">Hold… {Math.ceil((1 - progress) * (HOLD_MS / 1000))}s</span> : null}
      </div>
      {toast ? (
        <div className="sos-toast" role="status">
          {toast}
        </div>
      ) : null}
    </>
  );
}

export default GlobalSOSButton;
